/**
 * Utility functions for handling API responses consistently across the application
 * Handles both old and new API response formats
 */

/**
 * Extract data from API response safely
 * Handles both legacy format and new ApiResponse format
 * @param {*} response - The API response
 * @param {string} fallbackProperty - Property to check if data is not directly available
 * @returns {*} The extracted data
 */
export const extractResponseData = (response, fallbackProperty = null) => {
  if (!response) return null;

  // Check if this is a new format response with _meta
  if (response._meta && response._meta.success) {
    // New format: data might be in 'data' property or spread across the object
    if (response.data !== undefined) {
      return response.data;
    }
    // Remove _meta and return the rest
    const { _meta, ...data } = response;
    return data;
  }

  // Check if this is old format response with direct data property
  if (response.data !== undefined) {
    return response.data;
  }

  // Check fallback property if specified
  if (fallbackProperty && response[fallbackProperty] !== undefined) {
    return response[fallbackProperty];
  }

  // Return response as-is for legacy compatibility
  return response;
};

/**
 * Extract message from API response
 * @param {*} response - The API response
 * @returns {string|null} The message if available
 */
export const extractResponseMessage = (response) => {
  if (!response) return null;

  // Check new format with _meta
  if (response._meta && response._meta.message) {
    return response._meta.message;
  }

  // Check direct message property
  if (response.message) {
    return response.message;
  }

  return null;
};

/**
 * Extract metadata from API response
 * @param {*} response - The API response
 * @returns {object|null} The metadata if available
 */
export const extractResponseMeta = (response) => {
  if (!response) return null;

  // Check new format with _meta
  if (response._meta) {
    return response._meta.meta || response._meta;
  }

  // Check direct meta property
  if (response.meta) {
    return response.meta;
  }

  return null;
};

/**
 * Check if response indicates success
 * @param {*} response - The API response
 * @returns {boolean} True if response indicates success
 */
export const isResponseSuccessful = (response) => {
  if (!response) return false;

  // Check new format with _meta
  if (response._meta) {
    return response._meta.success === true;
  }

  // Check direct success property
  if (response.success !== undefined) {
    return response.success === true;
  }

  // If no explicit success indicator, assume success if response exists
  return true;
};

/**
 * Extract pagination info from API response
 * @param {*} response - The API response
 * @returns {object|null} Pagination info if available
 */
export const extractPaginationInfo = (response) => {
  const meta = extractResponseMeta(response);
  if (!meta) return null;

  // Common pagination properties
  const paginationProps = ['page', 'per_page', 'total', 'total_pages', 'has_next', 'has_prev'];
  const pagination = {};
  let hasPagination = false;

  paginationProps.forEach(prop => {
    if (meta[prop] !== undefined) {
      pagination[prop] = meta[prop];
      hasPagination = true;
    }
  });

  return hasPagination ? pagination : null;
};

/**
 * Handle array responses that might be wrapped in data property
 * @param {*} response - The API response
 * @param {string} arrayProperty - Name of the array property (e.g., 'items', 'results')
 * @returns {Array} The array data
 */
export const extractArrayData = (response, arrayProperty = null) => {
  const data = extractResponseData(response);
  
  if (Array.isArray(data)) {
    return data;
  }

  if (arrayProperty && data && Array.isArray(data[arrayProperty])) {
    return data[arrayProperty];
  }

  // Check common array property names
  const commonArrayProps = ['items', 'results', 'data', 'list'];
  for (const prop of commonArrayProps) {
    if (data && Array.isArray(data[prop])) {
      return data[prop];
    }
  }

  // If data is an object, return empty array as fallback
  if (typeof data === 'object' && data !== null) {
    return [];
  }

  // Return as array if not already
  return Array.isArray(data) ? data : [];
};

/**
 * Comprehensive response handler that extracts all useful information
 * @param {*} response - The API response
 * @returns {object} Object with data, message, meta, success, pagination
 */
export const handleApiResponse = (response) => {
  return {
    data: extractResponseData(response),
    message: extractResponseMessage(response),
    meta: extractResponseMeta(response),
    success: isResponseSuccessful(response),
    pagination: extractPaginationInfo(response)
  };
};

export default {
  extractResponseData,
  extractResponseMessage,
  extractResponseMeta,
  isResponseSuccessful,
  extractPaginationInfo,
  extractArrayData,
  handleApiResponse
};